import React, { useCallback, useEffect, useState } from 'react';
import http from 'Utils/http';
import { NavLink as RouterLink, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Box, AppBar, Toolbar, Typography, Container, Link } from '@mui/material';
import { logout } from 'Actions/auth';
import { ROUTES } from 'Constants/routes';
import { setLoading } from 'Actions/loading';
import { LogoutComponent } from 'Shared/LogoutComponent';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import Logo_PNG from '../../assets/img/logo.png';

const Header = () => {
    const access = useSelector((state) => state.auth.user.access);
    const currentUser = useSelector((state) => state.auth.user);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const permissions = currentUser?.permissions || [];
    const isAdmin = currentUser?.access === 'Admin';
    const isUsersRoot = location.pathname === ROUTES.USERS || location.pathname === `${ROUTES.USERS}/`;
    const userIdMatch = location.pathname.match(/^\/users\/(\d+)$/);
    const currentUserIdStr = currentUser && currentUser.id !== undefined && currentUser.id !== null ? String(currentUser.id) : null;
    const matchedUserIdStr = userIdMatch ? userIdMatch[1] : null;
    const isOwnUserPage = Boolean(currentUserIdStr && matchedUserIdStr && currentUserIdStr === matchedUserIdStr);
    const isUsersActive = Boolean((isUsersRoot || (userIdMatch && !isOwnUserPage)) && !isOwnUserPage);



    // Function to remove specific search parameter and navigate
    const removeSearchParam = useCallback(
        (key) => {
            const searchParamsCopy = new URLSearchParams(location.search);
            searchParamsCopy.delete(key);
            const newLocation = { ...location, search: searchParamsCopy.toString() };
            navigate(newLocation);
        },
        [location, navigate]
    );

    // Use useEffect to trigger actions based on search parameters
    useEffect(() => {
        const getSearchParamsWithPrefixAndPlaceholder = (prefix) => {
            searchParams.forEach((value, key) => {
                const match = key.match(new RegExp(`^${prefix}_(\\w+)$`));
                if (match) {
                    const paramName = match[1];
                    enqueueSnackbar(value, {
                        action: CustomSnackbar,
                        variant: paramName,
                    });
                    removeSearchParam(key);
                }
            });
        };
        getSearchParamsWithPrefixAndPlaceholder('massage');

        // Function to handle 'massage' search parameter
        const message = searchParams.get('massage');
        if (message) {
            enqueueSnackbar(message, {
                action: CustomSnackbar,
                variant: 'info',
            });
            removeSearchParam('massage');
        }

        // Function to handle 'action' search parameter
        const action = searchParams.get('action');
        if (action === 'logout') {
            dispatch(setLoading(true));
            http.post('/logout').finally(() => {
                dispatch(logout(navigate));
                dispatch(setLoading(false));
                navigate(ROUTES.HOME);
            });
        }
    }, [searchParams, enqueueSnackbar, dispatch, navigate, removeSearchParam]);

    const [firstName, setFirstName] = useState('');

    useEffect(() => {
        if (currentUser) {
            setFirstName(currentUser.first_name);
        }
    }, [currentUser, setFirstName]);

    return (
        <AppBar position='static'>
            <Container maxWidth={false}>
                <Toolbar
                    sx={{
                        '&.MuiToolbar-root': {
                            minHeight: '76px',
                            padding: '8px 0',
                        },
                    }}
                    disableGutters
                >
                    <Link
                        component={RouterLink}
                        to={ROUTES.HOME}
                        underline='none'
                        sx={{
                            marginRight: '25px',
                            marginLeft: '-20px',
                            lineHeight: 'unset',
                        }}
                    >
                        <Box
                            component='img'
                            sx={{
                                height: 60,
                                width: 60,
                            }}
                            alt='Infintegration'
                            src={Logo_PNG}
                        />
                    </Link>

                    {!isAuthenticated && (
                        <>
                            <Typography component='div'>
                                <Link
                                    className='nav-link'
                                    component={RouterLink}
                                    to={ROUTES.LOGIN}
                                    underline='none'
                                    color='primary.white'
                                    sx={{ marginRight: '30px' }}
                                >
                                    LOGIN
                                </Link>
                            </Typography>

                            <Typography component='div'>
                                <Link
                                    className='nav-link'
                                    component={RouterLink}
                                    to={ROUTES.REGISTRATION}
                                    underline='none'
                                    color='primary.white'
                                    sx={{ marginRight: '30px' }}
                                >
                                    REGISTRATION
                                </Link>
                            </Typography>
                        </>
                    )}


                    {/* FOR ADMIN */}
                    {isAuthenticated && (
                        <>
                            {(isAdmin || permissions.includes('MENU_INTEGRATIONS')) && (
                                <Typography component='div'>
                                    <Link
                                        className='nav-link'
                                        component={RouterLink}
                                        to={ROUTES.INTEGRATIONS}
                                        underline='none'
                                        color='primary.white'
                                        sx={{ marginRight: '30px' }}
                                    >
                                        INTEGRATIONS
                                    </Link>
                                </Typography>
                            )}
                            {(isAdmin || permissions.includes('MENU_FLOWS')) && (
                                <Typography component='div'>
                                    <Link
                                        className='nav-link'
                                        component={RouterLink}
                                        to={ROUTES.FLOWS}
                                        underline='none'
                                        color='primary.white'
                                        sx={{ marginRight: '30px' }}
                                    >
                                        FLOWS
                                    </Link>
                                </Typography>
                            )}
                            {(isAdmin || permissions.includes('MENU_LOGGING')) && (
                                <Typography component='div'>
                                    <Link
                                        className='nav-link'
                                        component={RouterLink}
                                        to={ROUTES.LOGGING}
                                        underline='none'
                                        color='primary.white'
                                        sx={{ marginRight: '30px' }}
                                    >
                                        LOGGING
                                    </Link>
                                </Typography>
                            )}
                            {(isAdmin || permissions.includes('MENU_BACKGROUND_FLOWS')) && (
                                <Typography component='div'>
                                    <Link
                                        className='nav-link'
                                        component={RouterLink}
                                        to={ROUTES.BACKGROUND_FLOWS}
                                        underline='none'
                                        color='primary.white'
                                        sx={{ marginRight: '30px' }}
                                    >
                                        BACKGROUND FLOWS
                                    </Link>
                                </Typography>
                            )}
                            {(isAdmin || permissions.includes('MENU_LOAN_BOARDING_HUB')) && (
                                <Typography component='div'>
                                    <Link
                                        className='nav-link'
                                        component={RouterLink}
                                        to={ROUTES.LOAN_BOARDING}
                                        underline='none'
                                        color='primary.white'
                                        sx={{ marginRight: '30px' }}
                                    >
                                        LOAN BOARDING HUB
                                    </Link>
                                </Typography>
                            )}
                        </>
                    )}

                    {/* FOR ADMIN */}
                    {(access === 'Admin' || access === 'Company Admin') && (
                        <>
                            {isAdmin && (
                                <Typography component='div'>
                                    <Link
                                        className='nav-link'
                                        component={RouterLink}
                                        to={ROUTES.TELEMETRY}
                                        underline='none'
                                        color='primary.white'
                                        sx={{ marginRight: '30px' }}
                                    >
                                        TELEMETRY
                                    </Link>
                                </Typography>
                            )}
                            {isAdmin && (
                                <Typography component='div'>
                                    <Link
                                        className='nav-link'
                                        component={RouterLink}
                                        to={ROUTES.USAGE_STATS}
                                        underline='none'
                                        color='primary.white'
                                        sx={{ marginRight: '30px' }}
                                    >
                                        USAGE STATS
                                    </Link>
                                </Typography>
                            )}
                            {(isAdmin || permissions.includes('MENU_USER_MANAGMENT')) && (
                                <Typography component='div'>
                                    <Link
                                        className={`nav-link${isUsersActive ? ' active' : ''}`}
                                        component={RouterLink}
                                        to={ROUTES.USERS}
                                        underline='none'
                                        color='primary.white'
                                        sx={{ marginRight: '30px' }}
                                        aria-current={isUsersActive ? 'page' : undefined}
                                    >
                                        USERS
                                    </Link>
                                </Typography>
                            )}
                            <Typography component='div'>
                                <Link
                                    className='nav-link'
                                    component={RouterLink}
                                    to={ROUTES.API_CLIENTS}
                                    underline='none'
                                    color='primary.white'
                                    sx={{ marginRight: '30px' }}
                                >
                                    API CLIENTS
                                </Link>
                            </Typography>
                        </>
                    )}

                    {isAuthenticated && (
                        <>
                            <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                                <Typography component='div'>
                                <Link
                                    className='nav-link'
                                    component={RouterLink}
                                    to={access === 'User' ? ROUTES.MY_ACCOUNT : `/users/${currentUser?.id}`}
                                    underline='none'
                                    color='primary.white'
                                    sx={{ marginRight: '20px', fontWeight: 400, fontSize: '1rem', fontFamily: 'inherit' }}
                                >
                                    {(firstName || currentUser?.last_name) ? `${firstName || ''}${firstName && currentUser?.last_name ? ' ' : ''}${currentUser?.last_name || ''}`.trim() : (currentUser?.email || 'MY ACCOUNT')}
                                </Link>
                            </Typography>
                            <LogoutComponent />
                            </Box>
                        </>
                    )}
                </Toolbar>
            </Container>
        </AppBar>
    );
};

export default Header;
